import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Global, css } from '@emotion/react'
import {
  decodeInterviewToken,
  getRawJWTFromToken,
} from '../utilities/tokenUtils'
import { Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import {
  useHMSStore,
  useHMSActions,
  selectIsConnectedToRoom,
  selectLocalVideoTrackID,
  useVideo,
} from '@100mslive/react-sdk'
import {
  VideoContainer,
  TopControls,
  ModernButton,
  DestructiveButton,
  OutlineIconButton,
  ProgressDot,
  TimerBadge,
  RecordingIndicator,
  VideoOverlay,
  QuestionCard,
  QuestionHeader,
  QuestionLabel,
  QuestionText,
  ProgressDotsContainer,
  ActionButtonsContainer,
  ButtonsWrapper,
  PauseButton,
  ResumeButton,
  PermissionOverlay,
  PermissionMessage,
  PermissionButton,
  QuestionLabelContainer,
} from '../components/mobileVideoInterview/styled'
import { VoiceOverButton } from '../components/styled'
import CustomModal from '../components/customModal'
import Play from '../assets/svgs/Play'
import Pause from '../assets/svgs/Pause'
import StopCircle from '../assets/svgs/stopCircle'
import RefreshCw from '../assets/svgs/RefreshCw'
import CheckCircle from '../assets/svgs/CheckCircle'
import Clock from '../assets/svgs/clock'
import Volume2Icon from '../assets/svgs/volume2'
import apiInterviewVersions from '../services/api/interviewVersion'
import LoadingPage from '../components/loadingPage'
import apiHMSVideoControl from '../services/api/hmsVideoControl'
import useDeviceType from '../hooks/useDeviceType'
import { voiceOver } from '../utilities/voiceOver'

function MobileVideoInterview() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const hmsActions = useHMSActions()
  const candidateRoleRoomCode = localStorage.getItem('candidateRoleRoomCode')
  const candidateRoleRoomName = localStorage.getItem('candidateRoleRoomName')
  const isConnected = useHMSStore(selectIsConnectedToRoom)
  const localVideoTrackID = useHMSStore(selectLocalVideoTrackID)
  const { videoRef } = useVideo({ trackId: localVideoTrackID })
  const { interviewToken } = useParams()
  const rawJWT = getRawJWTFromToken(interviewToken)
  const interviewId = decodeInterviewToken(interviewToken).interview_version_id
  const [question, setQuestion] = useState('')
  const [questionsNumber, setQuestionsNumber] = useState(0)
  const [questionProgress, setQuestionProgress] = useState(1)
  const [idOfCurrentQuestions, setIdOfCurrentQuestions] = useState()
  const [recordingStatus, setRecordingStatus] = useState('notAnswered')
  const [isLoading, setIsLoading] = useState(false)
  const [minutesInterview, setMinutesInterview] = useState(15)
  const [secondsInterview, setSecondsInterview] = useState(0)
  const [showModal, setShowModal] = useState(false)
  const [isBlinking, setIsBlinking] = useState(false)
  const [currentQuestion, setCurrentQuestion] = useState('')
  const [isAnimating, setIsAnimating] = useState(false)
  const [animationKey, setAnimationKey] = useState(0)
  const [isJoining, setIsJoining] = useState(true)
  const [joinError, setJoinError] = useState(null)
  const [recordingTime, setRecordingTime] = useState(0)
  const [permissionDenied, setPermissionDenied] = useState(false)
  const [interviewType, setInterviewType] = useState('')
  const [enableVoiceOver, setEnableVoiceOver] = useState(false)
  const deviceType = useDeviceType()
  const recordingTimerRef = useRef(null)
  const permissionCheckRef = useRef(null)
  const roomId = localStorage.getItem('room_id')

  const isCoverLetter = interviewType === 'COVERLETTER'
  const maxRecordingTime = isCoverLetter ? 180 : null

  const handleClose = () => setShowModal(false)

  const handleVoiceOver = async () => {
    try {
      await voiceOver(idOfCurrentQuestions, currentQuestion)
    } catch {
      // voice over error
    }
  }

  const checkPermissions = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      })

      stream.getTracks().forEach((track) => track.stop())
      setPermissionDenied(false)
      return true
    } catch (err) {
      if (
        deviceType === 'mobile' &&
        (err.name === 'NotAllowedError' ||
          err.name === 'PermissionDeniedError' ||
          err.name === 'NotFoundError' ||
          err.name === 'DevicesNotFoundError')
      ) {
        setPermissionDenied(true)
      }
      return false
    }
  }

  const requestDevicePermissions = async () => {
    try {
      const result = await checkPermissions()
      if (result) {
        joinRoom()
      }
    } catch {
      // permission request error
    }
  }

  const joinRoom = async () => {
    setIsJoining(true)
    try {
      const authToken = await hmsActions.getAuthTokenByRoomCode({
        roomCode: candidateRoleRoomCode,
      })
      await hmsActions.join({
        userName: candidateRoleRoomName,
        authToken,
      })
      setIsJoining(false)
    } catch (e) {
      setIsJoining(false)
      if (e?.message?.includes('DeviceInUse')) {
        setJoinError(
          'Camera or microphone is in use by another application. Please close other apps and try again.',
        )
      } else if (
        e?.message?.includes('NotAllowedError') ||
        e?.message?.includes('Permission')
      ) {
        setPermissionDenied(true)
      } else {
        setJoinError('Failed to join the room. Please try again.')
      }
    }
  }

  const endRoom = async () => {
    try {
      const lock = false
      const reason = 'Interview has ended'
      await hmsActions.endRoom(lock, reason)
    } catch {
      // error captured
    }
  }

  const handleSubmitAnswer = async () => {
    setIsLoading(true)
    apiInterviewVersions
      .sendAnswer({
        interviewToken: rawJWT,
        questionId: idOfCurrentQuestions,
        video: `interview-versions/${interviewId}/Q-${idOfCurrentQuestions}.mp4`,
        audio: `interview-versions/${interviewId}/Q-${idOfCurrentQuestions}.wav`,
      })
      .then(() => {
        if (questionProgress === questionsNumber) {
          endRoom()
          localStorage.clear()
          navigate(0)
        } else
          apiInterviewVersions.getNextQuestion(rawJWT).then((res) => {
            setQuestion(res.question.toString())
            setQuestionProgress(questionProgress + 1)
            setIdOfCurrentQuestions(res.id)
            setRecordingStatus('notAnswered')
            setRecordingTime(0)
          })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const startRecording = useCallback(async () => {
    const hasPermissions = await checkPermissions()
    if (!hasPermissions) {
      return
    }

    try {
      setRecordingStatus('loading')
      const response = await apiHMSVideoControl.startRecording(
        idOfCurrentQuestions,
        roomId,
        interviewId,
      )
      if (response.success) {
        setRecordingStatus('recording')
        setRecordingTime(0)

        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current)
        }

        recordingTimerRef.current = setInterval(() => {
          setRecordingTime((prev) => {
            const newTime = prev + 1
            if (isCoverLetter && newTime >= maxRecordingTime) {
              clearInterval(recordingTimerRef.current)
              setRecordingStatus('maxTimeReached')
              stopRecording()
              return maxRecordingTime
            }
            return newTime
          })
        }, 1000)
      }
    } catch (error) {
      if (
        error.name === 'NotAllowedError' ||
        error.name === 'PermissionDeniedError'
      ) {
        setPermissionDenied(true)
        return
      }

      if (error?.response?.data?.message.includes('beam already started')) {
        setRecordingStatus('recording')
        setRecordingTime(0)

        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current)
        }

        recordingTimerRef.current = setInterval(() => {
          setRecordingTime((prev) => {
            const newTime = prev + 1
            if (isCoverLetter && newTime >= maxRecordingTime) {
              clearInterval(recordingTimerRef.current)
              setRecordingStatus('maxTimeReached')
              stopRecording()
              return maxRecordingTime
            }
            return newTime
          })
        }, 1000)
      }
    }
  }, [idOfCurrentQuestions, roomId, interviewId])

  const pauseRecording = useCallback(async () => {
    try {
      setRecordingStatus('pausing')
      const response = await apiHMSVideoControl.pauseRecording(roomId)
      if (response.success) {
        setRecordingStatus('paused')

        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current)
        }
      }
    } catch (error) {
      if (
        error.name === 'NotAllowedError' ||
        error.name === 'PermissionDeniedError'
      ) {
        setPermissionDenied(true)
      }
    }
  }, [roomId])

  const resumeRecording = useCallback(async () => {
    // Verify permissions before resuming
    const hasPermissions = await checkPermissions()
    if (!hasPermissions) {
      return
    }

    try {
      const response = await apiHMSVideoControl.resumeRecording(roomId)
      if (response.success) {
        setRecordingStatus('recording')

        // Resume timer
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current)
        }

        recordingTimerRef.current = setInterval(() => {
          setRecordingTime((prev) => {
            const newTime = prev + 1
            if (isCoverLetter && newTime >= maxRecordingTime) {
              clearInterval(recordingTimerRef.current)
              setRecordingStatus('maxTimeReached')
              stopRecording()
              return maxRecordingTime
            }
            return newTime
          })
        }, 1000)
      }
    } catch (error) {
      // Error resuming recording

      if (
        error.name === 'NotAllowedError' ||
        error.name === 'PermissionDeniedError'
      ) {
        setPermissionDenied(true)
        return
      }

      if (error?.response?.data?.message.includes('beam already started')) {
        setRecordingStatus('recording')

        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current)
        }

        recordingTimerRef.current = setInterval(() => {
          setRecordingTime((prev) => {
            const newTime = prev + 1
            if (isCoverLetter && newTime >= maxRecordingTime) {
              clearInterval(recordingTimerRef.current)
              setRecordingStatus('maxTimeReached')
              stopRecording()
              return maxRecordingTime
            }
            return newTime
          })
        }, 1000)
      }
    }
  }, [roomId])

  const stopRecording = useCallback(async () => {
    if (recordingTime < 5) {
      return
    }

    try {
      setRecordingStatus('stopping')

      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current)
      }

      const response = await apiHMSVideoControl.stopRecording(
        roomId,
        interviewId,
      )
      if (response.success) {
        setRecordingStatus('stopped')
      }
    } catch (error) {
      if (
        error.name === 'NotAllowedError' ||
        error.name === 'PermissionDeniedError'
      ) {
        setPermissionDenied(true)
      }
    }
  }, [roomId, interviewId, recordingTime])

  const handleChangeResponse = () => {
    setShowModal(true)
  }

  const confirmChangeResponse = async () => {
    setShowModal(false)
    setRecordingStatus('loading')
    startRecording()
  }

  useEffect(() => {
    if (deviceType === 'mobile' && isConnected) {
      checkPermissions()

      permissionCheckRef.current = setInterval(() => {
        checkPermissions()
      }, 3000)
    }

    return () => {
      if (permissionCheckRef.current) {
        clearInterval(permissionCheckRef.current)
      }
    }
  }, [deviceType, isConnected])

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!isConnected && !permissionDenied) {
      joinRoom()
    }
  }, [isConnected, permissionDenied])

  useEffect(() => {
    apiInterviewVersions.startInterview(rawJWT).then((data) => {
      if (data && data.remainingQuestion) {
        setQuestion(data.question.toString())
        setCurrentQuestion(data.question.toString())
        setQuestionProgress(data.index + 1)
        setIdOfCurrentQuestions(data.id)
        setRecordingStatus('notAnswered')
        setInterviewType(data.type)
        setEnableVoiceOver(data.enableVoiceOver)
        const duration = data.duration.split('.')[0].split(':')
        setMinutesInterview(parseInt(duration[1], 10))
        setSecondsInterview(parseInt(duration[2], 10))
      } else {
        localStorage.clear()
        navigate(0)
      }
    })
    apiInterviewVersions
      .getNumberOfQuestionsByInterview(rawJWT)
      .then((res) => setQuestionsNumber(res.questions))
  }, [rawJWT, navigate])

  useEffect(() => {
    if (enableVoiceOver && currentQuestion) {
      setTimeout(() => {
        handleVoiceOver()
      }, 1000)
    }
  }, [currentQuestion, enableVoiceOver])

  useEffect(() => {
    const countdownInterval = setInterval(async () => {
      if (secondsInterview > 0) {
        setSecondsInterview(secondsInterview - 1)
      }
      if (secondsInterview === 0) {
        if (minutesInterview === 0) {
          clearInterval(countdownInterval)
          const roomId = localStorage.getItem('room_id')
          const managementToken = localStorage.getItem('management_token')
          await apiHMSVideoControl.stopRecording(roomId, managementToken)
          localStorage.clear()
          navigate(0)
        } else {
          setMinutesInterview(minutesInterview - 1)
          setSecondsInterview(59)
        }
      }
    }, 1000)
    return () => {
      clearInterval(countdownInterval)
    }
  }, [recordingStatus, secondsInterview, minutesInterview, navigate])

  useEffect(() => {
    if (currentQuestion !== question && question !== '') {
      setIsAnimating(true)
      setAnimationKey((prev) => prev + 1)
      setTimeout(() => {
        setCurrentQuestion(question)
        setIsAnimating(false)
      }, 300)
    }
  }, [question, currentQuestion])

  useEffect(() => {
    if (recordingStatus === 'recording') {
      const interval = setInterval(() => {
        setIsBlinking((prev) => !prev)
      }, 800)
      return () => clearInterval(interval)
    }
    setIsBlinking(false)
  }, [recordingStatus])

  const renderActionButtons = () => {
    if (isLoading) {
      return (
        <ButtonsWrapper>
          <ModernButton disabled>
            <Spinner
              animation="border"
              size="sm"
              style={{ marginRight: '0.5rem' }}
            />
            {t('pages.loading')}
          </ModernButton>
        </ButtonsWrapper>
      )
    }
    if (recordingStatus === 'notAnswered' || recordingStatus === 'loading') {
      const isRecordingDisabled = recordingStatus === 'loading'
      return (
        <ButtonsWrapper>
          <ModernButton
            onClick={startRecording}
            className="primary"
            disabled={isRecordingDisabled}
          >
            {recordingStatus === 'loading' ? (
              <Spinner
                animation="border"
                size="sm"
                style={{ marginRight: '0.5rem' }}
              />
            ) : (
              <Play style={{ marginRight: '0.5rem' }} width={18} height={18} />
            )}
            {t('interview.startRecording')}
          </ModernButton>
        </ButtonsWrapper>
      )
    }
    if (recordingStatus === 'recording' || recordingStatus === 'pausing') {
      return (
        <ButtonsWrapper>
          <PauseButton
            onClick={pauseRecording}
            disabled={recordingStatus === 'pausing'}
          >
            {recordingStatus === 'pausing' ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <Pause size={20} />
            )}
          </PauseButton>
          <DestructiveButton
            onClick={stopRecording}
            disabled={recordingStatus === 'pausing' || recordingTime < 5}
          >
            <StopCircle
              style={{ marginRight: '0.5rem' }}
              width={18}
              height={18}
              color="#fff"
            />
            {t('interview.stopRecording')}
          </DestructiveButton>
        </ButtonsWrapper>
      )
    }
    if (recordingStatus === 'paused') {
      return (
        <ButtonsWrapper>
          <ResumeButton onClick={resumeRecording}>
            <Play style={{ marginRight: '0.5rem' }} size={18} />
            {t('interview.resumeRecording')}
          </ResumeButton>
        </ButtonsWrapper>
      )
    }
    if (recordingStatus === 'stopping') {
      return (
        <ButtonsWrapper>
          <ModernButton disabled>
            <Spinner
              animation="border"
              size="sm"
              style={{ marginRight: '0.5rem' }}
            />
            {t('interview.savingAnswer')}
          </ModernButton>
        </ButtonsWrapper>
      )
    }
    if (recordingStatus === 'stopped') {
      return (
        <ButtonsWrapper>
          <OutlineIconButton onClick={handleChangeResponse}>
            <RefreshCw width={18} height={18} />
          </OutlineIconButton>
          <ModernButton onClick={handleSubmitAnswer} className="primary">
            {questionProgress === questionsNumber ? (
              <>
                <CheckCircle
                  style={{ marginRight: '0.5rem' }}
                  width={18}
                  height={18}
                />
                {t('general.submit')}
              </>
            ) : (
              t('pages.nextQuestion')
            )}
          </ModernButton>
        </ButtonsWrapper>
      )
    }
    return null
  }

  const renderProgressDots = () => {
    return Array.from({ length: questionsNumber }).map((_, index) => (
      <ProgressDot
        key={index}
        active={index < questionProgress}
        current={index === questionProgress - 1}
      />
    ))
  }

  if (isJoining) return <LoadingPage />

  if (joinError && !permissionDenied)
    return <div style={{ padding: '20px', color: 'red' }}>{joinError}</div>

  if (!question) return <LoadingPage />

  const formattedTime = `${minutesInterview}:${secondsInterview < 10 ? `0${secondsInterview}` : secondsInterview}`

  return (
    <>
      <Global
        styles={css`
          html,
          body,
          #root {
            width: 100vw;
            height: 100vh;
            overflow: hidden;
            touch-action: none;
          }
        `}
      />
      {showModal && (
        <CustomModal
          show={showModal}
          handleClose={handleClose}
          handleSubmit={confirmChangeResponse}
          message={t('interview.editResponseQuestion')}
        />
      )}
      <VideoContainer>
        {localVideoTrackID ? (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backgroundColor: '#000',
            }}
          />
        )}
        <VideoOverlay />

        {permissionDenied && (
          <PermissionOverlay>
            <PermissionMessage>
              {t(
                'interview.permissionRequired',
                'Camera and microphone access is required for this interview. Please enable permissions to continue.',
              )}
            </PermissionMessage>
            <PermissionButton onClick={requestDevicePermissions}>
              {t('interview.enablePermissions', 'Enable Camera & Mic')}
            </PermissionButton>
          </PermissionOverlay>
        )}

        <TopControls>
          {recordingStatus === 'recording' && (
            <RecordingIndicator isBlinking={isBlinking}>REC</RecordingIndicator>
          )}
          <TimerBadge>
            <Clock size={14} style={{ marginRight: '4px' }} />
            {formattedTime}
          </TimerBadge>
          {isCoverLetter && recordingStatus === 'recording' && (
            <TimerBadge
              style={{
                backgroundColor:
                  recordingTime >= maxRecordingTime * 0.8
                    ? '#ff4444'
                    : '#007bff',
              }}
            >
              <Clock size={14} style={{ marginRight: '4px' }} />
              {Math.floor(recordingTime / 60)}:
              {(recordingTime % 60).toString().padStart(2, '0')}{' '}
              {t('interview.maxDuration', { duration: '3:00' })}
            </TimerBadge>
          )}
        </TopControls>
        <QuestionCard key={animationKey} isAnimating={isAnimating}>
          <QuestionHeader>
            <QuestionLabelContainer>
              <QuestionLabel>
                {t('interview.question')} {questionProgress}/{questionsNumber}
              </QuestionLabel>
              {enableVoiceOver && (
                <VoiceOverButton
                  onClick={handleVoiceOver}
                  title={t('interview.readQuestionAloud')}
                >
                  <Volume2Icon width="16" height="16" color="#ffffff" />
                </VoiceOverButton>
              )}
            </QuestionLabelContainer>
            <ProgressDotsContainer>
              {renderProgressDots()}
            </ProgressDotsContainer>
          </QuestionHeader>
          <div style={{ padding: '0 1.25rem 1.25rem 1.25rem' }}>
            <QuestionText style={{ padding: '0', margin: '0' }}>
              {currentQuestion}
            </QuestionText>
          </div>
        </QuestionCard>
        <ActionButtonsContainer>{renderActionButtons()}</ActionButtonsContainer>
      </VideoContainer>
    </>
  )
}

export default MobileVideoInterview
